import lottie from 'lottie-web';
import { loadFonts } from '@loaders/FontLoader';

export default class LoadScene extends Phaser.Scene {
  constructor() {
    super({ key: 'LoadScene' });
  }

  async preload() {
    this.load.json('loading-anim', 'assets/loadAnims/cuemath_loading.json');
    await loadFonts();
  }

  create() {
    this.loadingAnim = this.addLoaderLottie(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
    );

    this.queueGameAssets();
    this.load.start();

    this.load.on(
      'progress',
      () => {
        this.loadingAnim.anim.playSegments([0, 25], true);
      },
      this,
    );

    this.load.on(
      'complete',
      () => {
        this.loadingAnim.anim.loop = false;
        this.loadingAnim.anim.playSegments([26, 41], true);
        this.loadingAnim.anim.addEventListener('complete', () => {
          this.scene.start('StartScene');
        });
      },
      this,
    );
  }

  addLoaderLottie(_x, _y) {
    const lottieRef = this.add
      .dom(_x - this.cameras.main.width / 2, _y - this.cameras.main.height / 2, 'div')
      .setScale(0.7);

    const lottieDiv = lottieRef.node;
    lottieDiv.style.height = '100%';
    lottieDiv.style.width = '100%';

    const lottieAnim = lottie.loadAnimation({
      container: lottieDiv,
      renderer: 'svg',
      loop: true,
      autoplay: false,
      animationData: this.cache.json.get('loading-anim'),
    });

    return { anim: lottieAnim, dom: lottieRef };
  }

  // ==================== ASSET LOADS (carried over from original LoadingScene.js) ====================

  queueGameAssets() {
    this.startSceneAssetsLoad();
    this.tutorialSceneAssetsLoad();
    this.audioLoad();
    this.cardAnimationsLoad();
    this.questionCardsLoad();
    this.questionImageLoad();
    this.gameSceneAssetsLoad();
    this.endSceneAssetsLoad();
  }

  startSceneAssetsLoad() {
    this.load.image('startSceneBG', 'assets/StartSceneAssets/Start Screen.png');
    this.load.image('gameName', 'assets/StartSceneAssets/Game Name.png');
    this.load.image('playButton', 'assets/StartSceneAssets/Button CTA.png');
    this.load.multiatlas(
      'startAnim',
      'assets/StartSceneAssets/start anim/startAnim.json',
      'assets/StartSceneAssets/start anim',
    );
    this.load.image('playHover', 'assets/StartSceneAssets/playHover.png');
  }

  audioLoad() {
    this.load.audio('bgMusic', 'assets/Audio/bgmusic.mp3');
    this.load.audio('correctCard', 'assets/Audio/correctselection.mp3');
    this.load.audio('incorrectCard', 'assets/Audio/Incorrect.mp3');
    this.load.audio('cardDis', 'assets/Audio/Card distribution.mp3');
    this.load.audio('cardShuffleAudio', 'assets/Audio/Card Shuffle Sound.mp3');

    for (let i = 1; i < 5; i++) {
      this.load.audio('ta' + i, 'assets/Audio/ta' + i + '.mp3');
    }

    this.load.audio('masterCardFlip', 'assets/Audio/Master Card flip 1.mp3');
    this.load.audio('levelComplete', 'assets/Audio/level completion success.mp3');
    this.load.audio('cardHover', 'assets/Audio/card hover.wav');
    this.load.audio('yayAudio', 'assets/Audio/confetti.wav');
  }

  tutorialSceneAssetsLoad() {
    this.load.image('overlay', 'assets/TutorialSceneAssets/Overlay.png');
    this.load.image('mainBG', 'assets/TutorialSceneAssets/mainBG.png');
    for (let i = 1; i < 5; i++) {
      this.load.image('text-' + i, 'assets/TutorialSceneAssets/text-' + i + '.png');
    }
    this.load.image('cardBack', 'assets/TutorialSceneAssets/cardBack.png');
    this.load.image('hand', 'assets/TutorialSceneAssets/handIcon.png');

    this.load.multiatlas(
      'confettiAnim',
      'assets/card anims/confetti/confettiAnim.json',
      'assets/card anims/confetti',
    );
    this.load.image('correctCounter', 'assets/TutorialSceneAssets/correctCounter.png');
  }

  cardAnimationsLoad() {
    this.load.atlas(
      'correctCardFlip',
      'assets/card anims/correctCardFlipAnim.png',
      'assets/card anims/correctCardFlipAnim.json',
    );
    this.load.atlas(
      'shuffleCards',
      'assets/card anims/shuffleAnim.png',
      'assets/card anims/shuffleAnim.json',
    );
    this.load.atlas(
      'wrongCardFlip',
      'assets/card anims/wrongCardAnim.png',
      'assets/card anims/wrongCardAnim.json',
    );
    this.load.atlas(
      'cardsIn',
      'assets/card anims/deckInAnim.png',
      'assets/card anims/deckInAnim.json',
    );
  }

  questionCardsLoad() {
    for (let i = 1; i < 9; i++) {
      this.load.image(
        'card_' + i,
        'assets/TutorialSceneAssets/question_0 cards/card_' + i + '.png',
      );
    }
    this.load.image('masterCard_0', 'assets/TutorialSceneAssets/question_0 cards/master card.png');
  }

  questionImageLoad() {
    for (let i = 1; i < 11; i++) {
      for (let j = 1; j < 9; j++) {
        this.load.image(
          'card_' + i + '_' + j,
          'assets/GameSceneAssets/questions/Question_' + i + '/card_' + j + '.png',
        );
      }
    }

    for (let i = 1; i < 11; i++) {
      this.load.image(
        'masterCard_' + i,
        'assets/GameSceneAssets/questions/Question_' + i + '/master card.png',
      );
    }
  }

  gameSceneAssetsLoad() {
    this.load.atlas(
      'hintFinalAnim',
      'assets/GameSceneAssets/hintFinalAnim.png',
      'assets/GameSceneAssets/hintFinalAnim.json',
    );
    this.load.image('closeHint', 'assets/GameSceneAssets/closeHintIconhdpi.png');
    this.load.image('hint_1', 'assets/GameSceneAssets/hint_1.png');
    this.load.image('hint_2', 'assets/GameSceneAssets/hint_2.png');
  }

  endSceneAssetsLoad() {
    this.load.multiatlas(
      'spotlight',
      'assets/EndSceneAssets/spotlight/spotlightAnimNew.json',
      'assets/EndSceneAssets/spotlight',
    );
    this.load.multiatlas(
      'endConfetti',
      'assets/EndSceneAssets/confetti/confettiAnimNew.json',
      'assets/EndSceneAssets/confetti',
    );
    this.load.multiatlas(
      'rewardAnim',
      'assets/EndSceneAssets/rewardAnim/rewardAnim.json',
      'assets/EndSceneAssets/rewardAnim',
    );

    this.load.image('nextButton', 'assets/EndSceneAssets/next button.png');
    this.load.image('endScreenBG', 'assets/EndSceneAssets/endSceenBG.png');
    this.load.image('gameOver', 'assets/EndSceneAssets/Game Over.png');
    this.load.image('correctIcon', 'assets/EndSceneAssets/correctIcon.png');
    this.load.image('incorrectIcon', 'assets/EndSceneAssets/incorrectIcon.png');
    this.load.image('retryButton', 'assets/EndSceneAssets/retry button.png');
    this.load.image('hintIcon', 'assets/EndSceneAssets/hintIcon.png');

    this.load.image('okayButton', 'assets/EndSceneAssets/okaybutton.png');
    this.load.image('rewardEarned', 'assets/EndSceneAssets/Rewards earned!.png');
    this.load.image('rewardName', 'assets/EndSceneAssets/coins.png');
    this.load.image('retryHover', 'assets/EndSceneAssets/RetryHover.png');
    this.load.image('okayHover', 'assets/EndSceneAssets/okayHover.png');
    this.load.image('okayDisabled', 'assets/EndSceneAssets/okayDisabled.png');
    this.load.image('nextHover', 'assets/EndSceneAssets/next hover.png');
    this.load.image('youWon', 'assets/EndSceneAssets/You won.png');
  }

  update() {}
}
